import { Typography, Box, Paper, useMediaQuery } from "@mui/material";
import { Features } from "../../Enums/companyStatus";
import colors from "../../Constants/colors";
import { CompanyResponse } from "../../Models/company";
import { evaluationModel } from "../../Models/evaluationModel";
import { useCertifications } from "../../Query/useCertifications";
import { useQuestionLists } from "../../Query/useQuestions";
import BusinessIcon from "@mui/icons-material/Business";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ClassIcon from "@mui/icons-material/Class";
import QuizIcon from "@mui/icons-material/Quiz";
import theme from "../../Constants/theme";
import { useTranslation } from "react-i18next";
import { hasPermission } from "../../Helpers/authHelpers";
import { useTerminology } from "../../Core/useTerminology";
import { ComoButton } from "../Common/ComoButton";

interface EvaluateButtonProps {
  handleEvaluateClick: (
    questionListId: string,
    certificationId: string
  ) => Promise<void>;
  companyResponse: CompanyResponse | undefined;
  modelOptions: evaluationModel[];
  selectedCompanyId: number | "";
  selectedModelId: number | "";
  certificationId: string;
  questionListId: string;
  isButtonClicked: boolean;
  hasQuestionPermisson: boolean;
}

const EvaluateButton: React.FC<EvaluateButtonProps> = ({
  handleEvaluateClick,
  companyResponse,
  hasQuestionPermisson,
  modelOptions,
  selectedCompanyId,
  selectedModelId,
  certificationId,
  questionListId,
  isButtonClicked,
}) => {
  const { data: certifications } = useCertifications();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: questionLists } = useQuestionLists(
    certificationId ? Number(certificationId) : undefined
  );
  const { t } = useTranslation("evaluationPage");
  const terminology = useTerminology();

  const isDisabled =
    !selectedCompanyId ||
    selectedModelId === "" ||
    !certificationId ||
    (!questionListId && !hasPermission(Features.EVALUATION));

  const handleClick = async () => {
    await handleEvaluateClick(questionListId, certificationId);
  };

  const selectedCompany = companyResponse?.companies?.find(
    (company) => company.id === selectedCompanyId
  );
  const companyName = selectedCompany
    ? selectedCompany.name
    : t("evaluationPage.noEntitySelected", {
        entity: terminology.singular,
      });

  const selectedModel = modelOptions?.find(
    (model) => model.model_id === selectedModelId
  );
  const modelName = selectedModel
    ? selectedModel.model_name
    : t("evaluationPage.noModelSelected");

  const selectedCertCat = certifications?.find(
    (cert) => cert.id.toString() === certificationId
  );
  const certificationName = selectedCertCat
    ? selectedCertCat.name
    : t("evaluationPage.noCertificationSelected");

  const selectedQuestionList = questionLists?.question_lists.find(
    (list) => list.id.toString() === questionListId
  );

  const questionListName = selectedQuestionList
    ? selectedQuestionList.name
    : t("evaluationPage.noQuestionListSelected");

  const summaryItems = [
    {
      icon: <BusinessIcon sx={{ fontSize: isMobile ? "1.5rem" : "2rem" }} />,
      label: terminology.singular,
      value: companyName,
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: isMobile ? "1.5rem" : "2rem" }} />,
      label: t("evaluationPage.selectEvaluationModel"),
      value: modelName,
    },
    {
      icon: <ClassIcon sx={{ fontSize: isMobile ? "1.5rem" : "2rem" }} />,
      label: t("evaluationPage.selectCertificationCategory"),
      value: certificationName,
    },
    ...(hasQuestionPermisson
      ? [
          {
            icon: <QuizIcon sx={{ fontSize: isMobile ? "1.5rem" : "2rem" }} />,
            label: t("evaluationPage.selectQuestionList"),
            value: questionListName,
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            color: colors.primary_dark,
            marginBottom: 0,
          }}
        >
          {t("evaluationPage.evaluateCompany", {
            entity: terminology.singular,
          })}
        </Typography>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {summaryItems.map((item, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${colors.gray200}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                color: colors.accent,
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {item.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.gray600,
                  fontSize: "0.75rem",
                  display: "block",
                  mb: 0.5,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.primary_dark,
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.value}
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <ComoButton
          onClick={handleClick}
          disabled={isDisabled}
          className={isButtonClicked ? "fade-out" : ""}
        >
          {t("evaluationPage.evaluateButton")}
        </ComoButton>
      </Box>
    </Box>
  );
};

export default EvaluateButton;
